import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Public API - no credentials needed for reading
export const api = axios.create({
  baseURL: `${BACKEND_URL}/api`,
  headers: { 'Content-Type': 'application/json' }
});

// Authenticated API - sends cookies for admin/editor actions
export const authApi = axios.create({
  baseURL: `${BACKEND_URL}/api`,
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' }
});

// Public endpoints (no auth needed)
export const getArticles = (params) => api.get('/articles', { params });
export const getArticle = (id) => api.get(`/articles/${id}`);
export const getArticleBySlug = (slug) => api.get(`/articles/by-slug/${slug}`);
export const recordArticleView = (id) => api.post(`/articles/${id}/view`);
export const backfillSlugs = () => authApi.post('/admin/backfill-slugs');
export const getCategories = () => api.get('/categories');
export const getTags = () => api.get('/tags');
export const getComments = (articleId) => api.get(`/articles/${articleId}/comments`);
export const subscribe = (email) => api.post('/subscribe', { email });
export const seedData = () => api.post('/seed');
export const getSiteSettings = () => api.get('/settings');
export const getWeather = (city) => api.get('/weather', { params: { city } });

// Authenticated endpoints (admin/editor actions)
export const postComment = (articleId, data) => authApi.post(`/articles/${articleId}/comments`, data);
export const getAdminArticles = (params) => authApi.get('/admin/articles', { params });
export const createArticle = (data) => authApi.post('/articles', data);
export const updateArticle = (id, data) => authApi.put(`/articles/${id}`, data);
export const deleteArticle = (id) => authApi.delete(`/articles/${id}`);
export const getAdminDashboard = () => authApi.get('/admin/dashboard');
export const getEnhancedStats = () => authApi.get('/admin/stats/enhanced');
export const getUsers = () => authApi.get('/users');
export const updateUserRole = (userId, role) => authApi.put(`/users/${userId}/role`, { role });
export const createCategory = (data) => authApi.post('/categories', data);
export const updateCategory = (id, data) => authApi.put(`/categories/${id}`, data);
export const deleteCategory = (id) => authApi.delete(`/categories/${id}`);
export const createTag = (data) => authApi.post('/tags', data);
export const deleteTag = (id) => authApi.delete(`/tags/${id}`);

// AI
export const aiSummarize = (text) => authApi.post('/ai/summarize', { text });
export const aiTranslate = (text, targetLang) => authApi.post('/ai/translate', { text, target_lang: targetLang });

// Upload & Media Library
export const uploadFile = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return authApi.post('/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
};
export const getMediaLibrary = (params) => authApi.get('/media', { params });
export const deleteMedia = (id) => authApi.delete(`/media/${id}`);

// Article Workflow
export const createDraft = () => authApi.post('/articles/draft');
export const autoSaveArticle = (id, data) => authApi.post(`/articles/${id}/autosave`, data);
export const scheduleArticle = (id, scheduledAt) => authApi.post(`/articles/${id}/schedule`, { scheduled_at: scheduledAt });
export const unpublishArticle = (id) => authApi.post(`/articles/${id}/unpublish`);

// Notifications
export const getNotifications = (limit = 10) => api.get(`/notifications?limit=${limit}`);
export const createNotification = (data) => authApi.post('/notifications', data);

// Site Settings (read = public, write = admin)
export const updateSiteSettings = (data) => authApi.put('/settings', data);
export const updateHomepageSections = (sections) => authApi.put('/settings/homepage-sections', { sections });
export const updateHeaderSettings = (data) => authApi.put('/settings/header', data);
export const updateBreakingNewsSettings = (data) => authApi.put('/settings/breaking-news', data);
export const updateWeatherSettings = (data) => authApi.put('/settings/weather', data);
export const updateSliderSettings = (data) => authApi.put('/settings/slider', data);
export const updateElectionPanelSettings = (data) => authApi.put('/settings/election-panel', data);
export const updateCricketManualMatches = (matches) => authApi.put('/settings/cricket-manual', { matches });
export const reorderCategories = (orderedIds) => authApi.put('/categories/reorder', { ordered_ids: orderedIds });
export const resetHomepageSections = () => authApi.post('/settings/reset-homepage-sections', {});
export const resetHeaderMenu = () => authApi.post('/settings/reset-header-menu', {});

// Podcast System (read = public, write = admin)
export const getPodcasts = (params) => api.get('/podcasts', { params });
export const getPodcast = (id) => api.get(`/podcasts/${id}`);
export const createPodcast = (data) => authApi.post('/podcasts', data);
export const updatePodcast = (id, data) => authApi.put(`/podcasts/${id}`, data);
export const deletePodcast = (id) => authApi.delete(`/podcasts/${id}`);

// Shorts System (read = public, write = admin)
export const getShorts = (params) => api.get('/shorts', { params });
export const getShort = (id) => api.get(`/shorts/${id}`);
export const createShort = (data) => authApi.post('/shorts', data);
export const updateShort = (id, data) => authApi.put(`/shorts/${id}`, data);
export const deleteShort = (id) => authApi.delete(`/shorts/${id}`);

// Live Data Widgets
export const getLiveStocks = () => api.get('/live/stocks');
export const getLiveCricket = () => api.get('/live/cricket');

// Newsletter Management
export const getSubscribers = (params) => authApi.get('/newsletter/subscribers', { params });
export const removeSubscriber = (email) => authApi.delete(`/newsletter/subscribers/${encodeURIComponent(email)}`);
export const toggleSubscriber = (email) => authApi.put(`/newsletter/subscribers/${encodeURIComponent(email)}/toggle`);

// Media Library (already exists, alias)
export const getMedia = (params) => authApi.get('/media', { params });
export const deleteMediaItem = (id) => authApi.delete(`/media/${id}`);
export const fixBrokenImages = (data) => authApi.post('/admin/fix-images', data);
export const cleanupTestData = () => authApi.post('/admin/cleanup-test-data', {});
